import { FC, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Card, CardContent, CardActionArea, CircularProgress, Alert, Fade } from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { AIGenerationTypeQuery } from '../../../../services/query/AIGenerationTypeQuery';
import { AIGenerationType } from '../../../../types/AIGenerationType';
import { PageUrl } from '../../../../types/enums/pageUrl-enums';
import './styles/AIGenerationTypeSelector.scss';

export const AIGenerationTypeSelector: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const [types, setTypes] = useState<AIGenerationType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadGenerationTypes();
    }, []);

    const loadGenerationTypes = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await AIGenerationTypeQuery.GetActiveAIGenerationTypes();
            const items = response?.model || response?.data?.model || [];
            setTypes(Array.isArray(items) ? items : []);
        } catch (err) {
            console.error('Error loading AI generation types:', err);
            setError(t('mannequin.aiGeneration.typeSelector.loadError'));
        } finally {
            setLoading(false);
        }
    };

    const handleTypeSelect = (type: AIGenerationType) => {
        history.push({
            pathname: PageUrl.studioRoutes.aiGenerationHanger.url,
            search: `?type=${encodeURIComponent(type.code)}`,
            state: { typeName: type.name },
        });
    };

    const renderSvgIcon = (svgContent: string) => {
        if (!svgContent) return null;
        return <Box className="type-card__icon" dangerouslySetInnerHTML={{ __html: svgContent }} />;
    };

    if (loading) {
        return (
            <Box className="ai-generation-type-selector ai-generation-type-selector--loading">
                <CircularProgress size={48} />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    {t('mannequin.aiGeneration.typeSelector.loading')}
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box className="ai-generation-type-selector ai-generation-type-selector--error">
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box className="ai-generation-type-selector">
            <Box className="ai-generation-type-selector__header">
                <Typography variant="h4" className="ai-generation-type-selector__title">
                    {t('mannequin.aiGeneration.typeSelector.title')}
                </Typography>
                <Typography variant="body1" className="ai-generation-type-selector__description">
                    {t('mannequin.aiGeneration.typeSelector.description')}
                </Typography>
            </Box>

            <Box className="ai-generation-type-selector__grid">
                {types.map((type, index) => (
                    <Fade in key={type.id} timeout={300 + index * 150}>
                        <Card className="type-card" elevation={2}>
                            <CardActionArea className="type-card__action" onClick={() => handleTypeSelect(type)}>
                                <CardContent className="type-card__content">
                                    {type.iconSvg && renderSvgIcon(type.iconSvg)}
                                    <Typography variant="h6" className="type-card__name">
                                        {type.name}
                                    </Typography>
                                    {type.description && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            className="type-card__description"
                                        >
                                            {type.description}
                                        </Typography>
                                    )}
                                    <Box className="type-card__arrow">
                                        <ArrowForwardIcon />
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Fade>
                ))}
            </Box>

            {types.length === 0 && !loading && (
                <Box className="ai-generation-type-selector__empty">
                    <Typography variant="body1" color="text.secondary">
                        {t('mannequin.aiGeneration.typeSelector.noTypes')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
